import { Suspense, useEffect } from 'react'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { Providers } from './app/providers'
import { AuthInit, RequireAuth, RequireProjectAccess } from './shared/auth'
import { ErrorBoundary } from './shared/components/ErrorBoundary'
import { Layout } from './shared/components/Layout'
import { NotFound } from './shared/components/NotFound'
import { SuspenseFallback } from './shared/components/SuspenseFallback'
import { InitiativeProvider } from './shared/initiative'
import { clearLazyReloadFlag, lazyWithRetry } from './shared/lib/lazyWithRetry'
import { PreviewBridge } from './shared/preview/PreviewBridge'
import { PreviewRibbon } from './shared/preview/PreviewRibbon'
import { ProjectProvider } from './shared/project'

const MagicLinkLogin = lazyWithRetry(() =>
  import('./modules/auth/pages/MagicLinkLogin').then((m) => ({ default: m.MagicLinkLogin })),
)
const ProjectSelector = lazyWithRetry(() =>
  import('./modules/dashboard/pages/ProjectSelector').then((m) => ({ default: m.ProjectSelector })),
)
const ProjectDashboard = lazyWithRetry(() =>
  import('./modules/dashboard/pages/ProjectDashboard').then((m) => ({
    default: m.ProjectDashboard,
  })),
)
const ProductProgress = lazyWithRetry(() =>
  import('./modules/progress/pages/ProductProgress').then((m) => ({ default: m.ProductProgress })),
)
const TestingArea = lazyWithRetry(() =>
  import('./modules/testing/pages/TestingArea').then((m) => ({ default: m.TestingArea })),
)
const AdminPage = lazyWithRetry(() =>
  import('./modules/admin/pages/AdminPage').then((m) => ({ default: m.AdminPage })),
)
const DiscoveryPage = lazyWithRetry(() =>
  import('./modules/discovery/pages/DiscoveryPage').then((m) => ({ default: m.DiscoveryPage })),
)
const ProfilePage = lazyWithRetry(() =>
  import('./modules/profile/pages/ProfilePage').then((m) => ({ default: m.ProfilePage })),
)
const InitiativeDocsPage = lazyWithRetry(() =>
  import('./modules/initiative-docs/pages/InitiativeDocsPage').then((m) => ({
    default: m.InitiativeDocsPage,
  })),
)
const AtendimentoArea = lazyWithRetry(() =>
  import('./modules/atendimento/pages/AtendimentoArea').then((m) => ({
    default: m.AtendimentoArea,
  })),
)

export default function App() {
  // App montou com sucesso -> libera o flag de reload pra futuras
  // falhas de chunk poderem reciclar tambem.
  useEffect(() => {
    clearLazyReloadFlag()
  }, [])

  return (
    <Providers>
      <AuthInit />
      <PreviewBridge />
      <PreviewRibbon />
      <BrowserRouter>
        <ErrorBoundary>
          <Suspense fallback={<SuspenseFallback />}>
            <Routes>
              <Route path="/login" element={<MagicLinkLogin />} />

              <Route
                path="/"
                element={
                  <RequireAuth>
                    <ProjectSelector />
                  </RequireAuth>
                }
              />

              <Route
                path="/projeto/:id"
                element={
                  <RequireAuth>
                    <RequireProjectAccess>
                      <ProjectProvider>
                        <Layout />
                      </ProjectProvider>
                    </RequireProjectAccess>
                  </RequireAuth>
                }
              >
                <Route index element={<ProjectDashboard />} />
                <Route path="descoberta" element={<DiscoveryPage />} />
                <Route path="progresso" element={<ProductProgress />} />
                <Route path="testes" element={<TestingArea />} />
                {/* ADR-081: o historico virou a aba Historico do Progresso da
                    iniciativa. As rotas antigas levam pra la. */}
                <Route path="historico" element={<Navigate to=".." replace />} />
                <Route path="docs" element={<Navigate to=".." replace />} />
                <Route path="admin" element={<AdminPage />} />
                <Route path="perfil" element={<ProfilePage />} />
              </Route>

              <Route
                path="/iniciativa/:iid"
                element={
                  <RequireAuth>
                    <InitiativeProvider>
                      <Layout />
                    </InitiativeProvider>
                  </RequireAuth>
                }
              >
                <Route index element={<Navigate to="progresso" replace />} />
                <Route path="descoberta" element={<DiscoveryPage />} />
                <Route path="progresso" element={<ProductProgress />} />
                <Route path="atendimento" element={<AtendimentoArea />} />
                {/* as duas abas viraram uma: os links antigos continuam abrindo
                    a tela nova, para nao quebrar bookmark de cliente */}
                <Route path="validacao" element={<Navigate to="../atendimento" replace />} />
                <Route path="suporte" element={<Navigate to="../atendimento" replace />} />
                <Route path="docs" element={<InitiativeDocsPage />} />
              </Route>

              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </BrowserRouter>
    </Providers>
  )
}
